import {
    RemoteConstantsModel,
    Metadata,
    UnitData,
    BuildingData,
    TileData,
    ResourceData,
    TechTreeComponent,
} from "../models/remoteConstants.model";
import {diffRecord, FieldChange, recordsEqual} from "./recordDiff";
import {
    diffGlobalsSection,
    diffImagePacksSection,
    diffConstructionImagePacksSection,
    diffImagesSection,
    diffLocalizationSection,
    diffSoundsSection,
} from "./extraSections";

type NameResolver = (key: string) => string;

// Union of every id/uid recognised across both branches, used to decide
// whether a string in a field path or value is an opaque uid that should be
// run through the name resolver for display.
function collectKnownIds(model: RemoteConstantsModel): Set<string> {
    const ids = new Set<string>();
    model.units?.forEach(u => ids.add(u.id));
    model.buildings?.forEach(b => ids.add(b.id));
    model.tiles?.forEach(t => ids.add(t.id));
    model.resources?.forEach(r => ids.add(r.id));
    model.unitAttributes?.forEach(m => ids.add(m.uid));
    model.buildingAttributes?.forEach(m => ids.add(m.uid));
    model.techActionAttributes?.forEach(m => ids.add(m.uid));
    model.techPassiveAttributes?.forEach(m => ids.add(m.uid));
    model.rewards?.forEach(m => ids.add(m.uid));
    model.techTree?.forEach(n => ids.add(n.id));
    return ids;
}

function resolveIfKnown(v: unknown, known: Set<string>, resolve: NameResolver): unknown {
    return typeof v === 'string' && known.has(v) ? resolve(v) : v;
}

// Short prefix of a uid for disambiguation suffixes — long enough to be
// distinct in practice, short enough to read.
function uidHint(uid: string): string {
    return uid.length > 8 ? `${uid.substring(0, 8)}…` : uid;
}

function isObj(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// Array of inline BuildingReward objects (passiveRewards). Detected by shape so
// the opaque diff for the field can be rendered as a readable summary instead
// of a raw JSON blob.
function isPassiveRewardArray(v: unknown): v is Record<string, unknown>[] {
    return Array.isArray(v) && v.length > 0
        && v.every(e => isObj(e) && 'grant' in e && 'trigger' in e);
}

function describeEveryN(t: Record<string, unknown>): string {
    const cadence = Number(t.cadence);
    const base = cadence === 1 ? 'every turn' : `every ${cadence} turns`;
    const delay = Number(t.firstTriggerDelay);
    const delayStr = delay > 0 ? ` (after ${delay})` : '';
    return `${base}${delayStr}`;
}

function describeTrigger(t: unknown, known: Set<string>, resolve: NameResolver): string {
    if (!isObj(t)) return '';
    const name = (id: unknown) => (typeof id === 'string' && known.has(id) ? resolve(id) : String(id));
    switch (t.kind) {
        case 'OnPlace':          return 'on placement';
        case 'OnUpgrade':        return 'on upgrade';
        case 'EveryNTurns':      return describeEveryN(t);
        case 'PerAdjacent':      return `per adjacent ${name(t.adjacentTo)}`;
        case 'PerAdjacentOnce':  return `per adjacent ${name(t.adjacentTo)} (once)`;
        case 'TilesExplored':    return `per ${t.cloudCount} tiles explored`;
        case 'FullTurnOccupied': return 'per full turn occupied';
        case 'BuiltOnTile':      return `${describeEveryN(t)} on ${name(t.containing)}`;
        default:                 return typeof t.kind === 'string' ? t.kind : '';
    }
}

function describeReward(r: Record<string, unknown>, known: Set<string>, resolve: NameResolver): string {
    const grant = isObj(r.grant) ? r.grant : {};
    const amount = Number(grant.amount);
    const sign = amount >= 0 ? '+' : '';
    const grantStr = `${grant.kind ?? '?'} ${sign}${Number.isFinite(amount) ? amount : grant.amount}`;
    const triggerStr = describeTrigger(r.trigger, known, resolve);
    let s = [grantStr, triggerStr].filter(Boolean).join(' ');
    const ramp = Number(r.amountIncreasePerTrigger);
    if (ramp) s += ` (+${ramp}/trigger)`;
    if (r.active === false) s += ' [inactive]';
    return s;
}

function summarizePassiveRewards(arr: Record<string, unknown>[], known: Set<string>, resolve: NameResolver): string {
    return arr.map(r => describeReward(r, known, resolve)).join(' · ');
}

function localizeFieldChange(fc: FieldChange, known: Set<string>, resolve: NameResolver): FieldChange {
    const displayPath = fc.path
        .map(seg => known.has(seg) ? resolve(seg) : seg)
        .join('.');

    if (fc.kind === 'set') {
        // Pre-pass to populate the "seen" map across both sides so we can
        // detect cross-side label collisions consistently.
        const seen = new Map<string, string>();
        const allRaw = [...(fc.added ?? []), ...(fc.removed ?? [])];
        for (const raw of allRaw) {
            if (!known.has(raw)) continue;
            const label = resolve(raw);
            const prev = seen.get(label);
            if (prev === undefined) seen.set(label, raw);
            else if (prev !== raw) seen.set(label, `__AMBIGUOUS__`);
        }
        const labelSetEntry = (raw: string): string => {
            if (!known.has(raw)) return raw;
            const label = resolve(raw);
            return seen.get(label) === '__AMBIGUOUS__'
                ? `${label} (${uidHint(raw)})`
                : label;
        };
        return {
            ...fc,
            displayPath,
            displayAdded: fc.added?.map(labelSetEntry),
            displayRemoved: fc.removed?.map(labelSetEntry),
        };
    }

    if (fc.kind === 'objset') {
        // Element-wise reward diff — summarize each added / removed reward so
        // the row reads like a set change of human-readable reward descriptions.
        return {
            ...fc,
            displayPath,
            displayAdded: (fc.objAdded ?? []).map(r => describeReward(r, known, resolve)),
            displayRemoved: (fc.objRemoved ?? []).map(r => describeReward(r, known, resolve)),
        };
    }

    if (fc.kind === 'opaque' && (isPassiveRewardArray(fc.current) || isPassiveRewardArray(fc.target))) {
        return {
            ...fc,
            displayPath,
            displayCurrent: isPassiveRewardArray(fc.current) ? summarizePassiveRewards(fc.current, known, resolve) : fc.current,
            displayTarget:  isPassiveRewardArray(fc.target)  ? summarizePassiveRewards(fc.target,  known, resolve) : fc.target,
        };
    }

    let displayCurrent = resolveIfKnown(fc.current, known, resolve);
    let displayTarget  = resolveIfKnown(fc.target, known, resolve);

    // Same-label-different-uid: both sides resolved to identical display
    // strings but their raw uid values differ. Without disambiguation the
    // diff row looks like `Espionage → Espionage`, hiding that the
    // references are actually pointing at different records (the canonical
    // case is when two branches each created their own "Espionage" tech
    // node with a separately-generated uid). Append a uid hint to each.
    const currentRaw = typeof fc.current === 'string' ? fc.current : null;
    const targetRaw  = typeof fc.target  === 'string' ? fc.target  : null;
    if (displayCurrent === displayTarget
        && currentRaw && targetRaw
        && currentRaw !== targetRaw
        && known.has(currentRaw) && known.has(targetRaw)) {
        displayCurrent = `${displayCurrent} (${uidHint(currentRaw)})`;
        displayTarget  = `${displayTarget} (${uidHint(targetRaw)})`;
    }

    return {
        ...fc,
        displayPath,
        displayCurrent,
        displayTarget,
    };
}

// Discriminator for which slice of state a section reads from. Used by the
// per-difference Resolve flow to find the matching record in Current and
// apply writes against the right collection.
//
// RecordSectionKey  — array-of-records sections (the original ten). Matched by
//                     uid (metadata) or id (entities); whole-record + per-field
//                     migration via the record/resolve commands.
// MapSectionKey     — key→value map sections (localization, sounds, images,
//                     imagePacks, constructionImagePacks). Each key is an entry;
//                     whole-value migrate / remove via the map-entry commands.
// GlobalsSectionKey — the singleton "Game Settings" pseudo-section: the model's
//                     top-level scalars + nested config objects, per-field
//                     resolved via the global-field command.
export type RecordSectionKey =
    | 'unitAttributes' | 'buildingAttributes' | 'techActionAttributes'
    | 'techPassiveAttributes' | 'rewards'
    | 'units' | 'buildings' | 'tiles' | 'resources' | 'techTree';

export type MapSectionKey = 'localization' | 'sounds' | 'images' | 'imagePacks' | 'constructionImagePacks';

export type GlobalsSectionKey = 'globals';

export type SectionKey = RecordSectionKey | MapSectionKey | GlobalsSectionKey;

export type SectionFlavor = 'record' | 'map' | 'globals';

const RECORD_SECTION_KEYS: RecordSectionKey[] = [
    'unitAttributes', 'buildingAttributes', 'techActionAttributes',
    'techPassiveAttributes', 'rewards',
    'units', 'buildings', 'tiles', 'resources', 'techTree',
];

export function isRecordSection(key: SectionKey): key is RecordSectionKey {
    return (RECORD_SECTION_KEYS as string[]).includes(key);
}

// A summary row in a section's onlyInCurrent / onlyInTarget list.
export interface SectionEntry {
    identity: string;     // stable key: uid/id for records, the map key for maps
    name: string;         // human-readable display name
    // Map flavor only: the location of this entry's value within the map
    // section (e.g. ['en', 'unit.scout'] for localization, ['unit.scout'] for
    // a single-level map). Carried so the migrate/remove commands can write it.
    path?: string[];
}

export interface RecordDiff {
    identity: string;     // uid or id — the stable key used to match records across branches
    name: string;         // human-readable display name (localized where possible)
    fieldChanges: FieldChange[];
    // Map flavor only: the location of the entry within the map section (see
    // SectionEntry.path). Present on map "differs" rows so the value can be
    // pulled across wholesale.
    path?: string[];
    // Set when uid-matched records look like they might not be the same
    // concept (their `id` fields differ). Deterministic uids agree across
    // branches today because every branch went through the same canonical
    // migration, but counter-suffixed uids (reward.goldperturn.1) authored
    // INDEPENDENTLY on two branches can pair different instances.
    identityHint?: string;
}

export interface SectionDiff {
    title: string;
    sectionKey: SectionKey;
    flavor: SectionFlavor;
    onlyInCurrent: SectionEntry[];
    onlyInTarget: SectionEntry[];
    differs: RecordDiff[];
    identical: boolean;
}

export interface BranchDiff {
    sections: SectionDiff[];
}

// Metadata records carry a deterministic namespaced `uid` that is stable
// across branches (the same logical instance has the same uid everywhere,
// courtesy of the canonical uid migration). Match on `uid` directly and
// compare every other field — including `id`, since an id rename on one
// branch is a real difference. The old positional duplicate-id pairing
// heuristic is gone: duplicates carry distinct uids (.0/.1/.2).
function diffMetadataSection(
    title: string,
    sectionKey: SectionKey,
    current: Metadata[] | undefined,
    target: Metadata[] | undefined,
    resolveCurrent: NameResolver,
    resolveTarget: NameResolver,
    knownIds: Set<string>,
    resolveCombined: NameResolver,
): SectionDiff {
    const curr = current ?? [];
    const targ = target ?? [];

    const currByUid = new Map(curr.map(m => [m.uid, m]));
    const targByUid = new Map(targ.map(m => [m.uid, m]));

    const allUids = new Set<string>();
    currByUid.forEach((_v, k) => allUids.add(k));
    targByUid.forEach((_v, k) => allUids.add(k));

    const onlyInCurrent: { identity: string; name: string }[] = [];
    const onlyInTarget: { identity: string; name: string }[] = [];
    const differs: RecordDiff[] = [];

    const stripUid = (m: Metadata): Omit<Metadata, 'uid'> => {
        const {uid, ...rest} = m;
        return rest;
    };

    allUids.forEach(uid => {
        const c = currByUid.get(uid);
        const t = targByUid.get(uid);

        if (c && t) {
            const cs = stripUid(c);
            const ts = stripUid(t);
            if (recordsEqual(cs, ts)) return;
            const rawChanges = diffRecord(cs, ts);
            // `recordsEqual` is JSON-stringify based (order-sensitive); diffRecord
            // treats primitive arrays as sets. They can disagree when only array
            // order differs — skip those rows rather than show "0 fields".
            if (rawChanges.length === 0) return;
            differs.push({
                identity: uid,
                name: resolveCurrent(uid),
                fieldChanges: rawChanges.map(fc => localizeFieldChange(fc, knownIds, resolveCombined)),
                identityHint: c.id !== t.id
                    ? `id differs ("${c.id}" vs "${t.id}") — verify these are the same record before syncing`
                    : undefined,
            });
        } else if (c) {
            onlyInCurrent.push({identity: uid, name: resolveCurrent(uid)});
        } else if (t) {
            onlyInTarget.push({identity: uid, name: resolveTarget(uid)});
        }
    });

    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    onlyInCurrent.sort(byName);
    onlyInTarget.sort(byName);
    differs.sort(byName);

    return {
        title,
        sectionKey,
        flavor: 'record',
        onlyInCurrent,
        onlyInTarget,
        differs,
        identical: onlyInCurrent.length === 0 && onlyInTarget.length === 0 && differs.length === 0,
    };
}

function diffSection<T extends object>(
    title: string,
    sectionKey: SectionKey,
    current: T[] | undefined,
    target: T[] | undefined,
    keyOf: (record: T) => string,
    nameOf: (record: T, resolveName: NameResolver) => string,
    resolveCurrent: NameResolver,
    resolveTarget: NameResolver,
    knownIds: Set<string>,
    resolveCombined: NameResolver,
): SectionDiff {
    const curr = current ?? [];
    const targ = target ?? [];
    const currMap = new Map(curr.map(r => [keyOf(r), r]));
    const targMap = new Map(targ.map(r => [keyOf(r), r]));

    const onlyInCurrent: { identity: string; name: string }[] = [];
    const onlyInTarget: { identity: string; name: string }[] = [];
    const differs: RecordDiff[] = [];

    currMap.forEach((rec, key) => {
        const other = targMap.get(key);
        if (!other) {
            onlyInCurrent.push({identity: key, name: nameOf(rec, resolveCurrent)});
            return;
        }
        if (!recordsEqual(rec, other)) {
            const rawChanges = diffRecord(rec, other);
            // See note in diffMetadataSection: skip "no real changes" rows that
            // recordsEqual flagged purely due to array-order differences.
            if (rawChanges.length === 0) return;
            differs.push({
                identity: key,
                name: nameOf(rec, resolveCurrent),
                fieldChanges: rawChanges.map(fc => localizeFieldChange(fc, knownIds, resolveCombined)),
            });
        }
    });

    targMap.forEach((rec, key) => {
        if (!currMap.has(key)) {
            // Resolve names of target-only records using the target branch's
            // localization — current loc may not have entries for them.
            onlyInTarget.push({identity: key, name: nameOf(rec, resolveTarget)});
        }
    });

    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    onlyInCurrent.sort(byName);
    onlyInTarget.sort(byName);
    differs.sort(byName);

    return {
        title,
        sectionKey,
        flavor: 'record',
        onlyInCurrent,
        onlyInTarget,
        differs,
        identical: onlyInCurrent.length === 0 && onlyInTarget.length === 0 && differs.length === 0,
    };
}

export function diffBranches(
    current: RemoteConstantsModel,
    target: RemoteConstantsModel,
): BranchDiff {
    const currentLoc = current.localization?.['en'] ?? {};
    const targetLoc = target.localization?.['en'] ?? {};
    const resolveCurrent: NameResolver = key => currentLoc[key] ?? key;
    const resolveTarget: NameResolver = key => targetLoc[key] ?? key;
    // Combined resolver used when post-processing field changes — prefer
    // current branch's name, fall back to target's, fall back to the raw key.
    const resolveCombined: NameResolver = key => currentLoc[key] ?? targetLoc[key] ?? key;

    const knownIds = new Set<string>();
    collectKnownIds(current).forEach(id => knownIds.add(id));
    collectKnownIds(target).forEach(id => knownIds.add(id));

    const idKey = (e: { id: string }) => e.id;
    const idName = (e: { id: string }, r: NameResolver) => r(e.id);

    const entityArgs = [resolveCurrent, resolveTarget, knownIds, resolveCombined] as const;
    const metaArgs = [resolveCurrent, resolveTarget, knownIds, resolveCombined] as const;

    const sections: SectionDiff[] = [
        diffMetadataSection('Unit Attributes', 'unitAttributes', current.unitAttributes, target.unitAttributes, ...metaArgs),
        diffMetadataSection('Building Attributes', 'buildingAttributes', current.buildingAttributes, target.buildingAttributes, ...metaArgs),
        diffMetadataSection('Tech Action Attributes', 'techActionAttributes', current.techActionAttributes, target.techActionAttributes, ...metaArgs),
        diffMetadataSection('Tech Passive Attributes', 'techPassiveAttributes', current.techPassiveAttributes, target.techPassiveAttributes, ...metaArgs),
        diffMetadataSection('Rewards', 'rewards', current.rewards, target.rewards, ...metaArgs),
        diffSection<UnitData>('Units', 'units', current.units, target.units, idKey, idName, ...entityArgs),
        diffSection<BuildingData>('Buildings', 'buildings', current.buildings, target.buildings, idKey, idName, ...entityArgs),
        diffSection<TileData>('Tiles', 'tiles', current.tiles, target.tiles, idKey, idName, ...entityArgs),
        diffSection<ResourceData>('Resources', 'resources', current.resources, target.resources, idKey, idName, ...entityArgs),
        diffSection<TechTreeComponent>('Tech Tree', 'techTree', current.techTree, target.techTree, idKey, idName, ...entityArgs),
        diffGlobalsSection(current, target, resolveCombined),
        diffLocalizationSection(current, target),
        diffSoundsSection(current, target),
        diffImagesSection(current, target),
        diffImagePacksSection(current, target),
        diffConstructionImagePacksSection(current, target),
    ];

    return {sections};
}
